import { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FiFileText, FiHome, FiMap, FiMaximize2, FiStar, FiSun, FiUpload, FiVideo } from 'react-icons/fi';

const SLOTS = [
  'Fachada',
  'Entrada',
  'Living',
  'Cocina',
  'Baño',
  'Habitación',
  'Habitación secundaria',
  'Cochera',
  'Patio / Jardín',
  'Balcón / Terraza',
  'Vista',
  'Detalle',
  'Ambiente adicional 1',
  'Ambiente adicional 2',
  'Ambiente adicional 3',
  'Ambiente adicional 4',
  'Ambiente adicional 5',
  'Ambiente adicional 6',
  'Ambiente adicional 7',
  'Ambiente adicional 8'
];

export default function NewPropertyPhotos({ onBack, onSubmit, initialPhotos = [], initialVideo = null, initialFloorPlans = [], initialVirtualTourUrl = '' }) {

  const [photos, setPhotos] = useState(Array(SLOTS.length).fill(null));
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [video, setVideo] = useState(initialVideo || null);
  const [floorPlans, setFloorPlans] = useState(initialFloorPlans || []);
  const [virtualTourUrl, setVirtualTourUrl] = useState(initialVirtualTourUrl || '');
  const [planError, setPlanError] = useState('');
  const [videoError, setVideoError] = useState('');
  const [fileError, setFileError] = useState('');
  const [externalDrag, setExternalDrag] = useState(false);
  const [activeExtra, setActiveExtra] = useState(null);

  const inputRefs = useRef([]);
  const bulkInputRef = useRef(null);

  const openBulkPicker = () => bulkInputRef.current?.click();

  /* ===============================
     CARGAR FOTOS EXISTENTES (EDICIÓN)
     =============================== */

  useEffect(() => {

    if (!initialPhotos || initialPhotos.length === 0) return;

    setPhotos(prev => {

      const alreadyLoaded = prev.some(p => p !== null);
      if (alreadyLoaded) return prev;

      const next = Array(SLOTS.length).fill(null);

      initialPhotos.slice(0, SLOTS.length).forEach((img, i) => {

        const url =
          typeof img === "string"
            ? img
            : img.url || img.preview;

        next[i] = {
          preview: url,
          url: url,
          file: null
        };

      });

      return next;

    });

  }, [initialPhotos]);

  /* ===============================
     SUBIR ARCHIVOS
     =============================== */

  const addFiles = (files, forcedIndex = null) => {
    setFileError('');
    const incoming = Array.from(files || []).filter(file =>
      file.type?.startsWith('image/') || /\.(jpe?g|png|webp|avif|heic|heif)$/i.test(file.name || '')
    );
    if (!incoming.length) {
      setFileError('Arrastrá o seleccioná archivos de imagen.');
      return;
    }

    const next = [...photos];

    let slotIndex =
      forcedIndex !== null
        ? forcedIndex
        : next.findIndex(p => p === null);

    incoming.forEach(file => {

      if (slotIndex === -1) return;

      next[slotIndex] = {
        file,
        preview: URL.createObjectURL(file)
      };

      slotIndex = next.findIndex(p => p === null);

    });

    setPhotos(next);
    if (incoming.length > next.filter(Boolean).length - photos.filter(Boolean).length) {
      setFileError(`Podés publicar hasta ${SLOTS.length} fotos.`);
    }

  };

  /* ===============================
     ELIMINAR FOTO
     =============================== */

  const removePhoto = (index) => {

    const next = [...photos];
    if (next[index]?.file && next[index]?.preview?.startsWith('blob:')) URL.revokeObjectURL(next[index].preview);
    next.splice(index, 1);
    next.push(null);

    setPhotos(next);

    if (selectedIndex === index) setSelectedIndex(null);

  };

  const movePhoto = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= photos.length || !photos[targetIndex]) return;

    const next = [...photos];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    setPhotos(next);
    setSelectedIndex(targetIndex);
  };

  /* ===============================
     DRAG & DROP
     =============================== */

  const onDragStart = (_, index) => {
    setDragIndex(index);
    setSelectedIndex(index);
  };

  const onDragOver = (e, index) => {
    e.preventDefault();
    setOverIndex(index);
  };

  const onDropReorder = (index) => {

    if (dragIndex === null || dragIndex === index) return;

    const next = [...photos];

    [next[dragIndex], next[index]] =
      [next[index], next[dragIndex]];

    setPhotos(next);

    setDragIndex(null);
    setOverIndex(null);

  };

  /* ===============================
     CONTADOR
     =============================== */

  const filled = photos.filter(Boolean).length;

  const canContinue = filled >= 4;

  const addVideo = (file) => {
    setVideoError('');
    if (!file) return;
    if (!['video/mp4', 'video/quicktime', 'video/webm'].includes(file.type)) {
      setVideoError('Usá un archivo MP4, MOV o WebM.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setVideoError('El video no puede superar los 100 MB.');
      return;
    }
    setVideo({ file, preview: URL.createObjectURL(file) });
  };

  const addFloorPlans = (files) => {
    setPlanError('');
    const incoming = Array.from(files || []).filter((file) => (
      file.type === 'application/pdf' || file.type?.startsWith('image/')
    ));
    if (!incoming.length) return setPlanError('Seleccioná planos en PDF, JPG, PNG o WebP.');
    const available = Math.max(0, 3 - floorPlans.length);
    if (!available) return setPlanError('Podés cargar hasta 3 planos.');
    const accepted = incoming.slice(0, available).map((file) => ({
      file,
      type: file.type === 'application/pdf' ? 'pdf' : 'image',
      title: file.name.replace(/\.[^.]+$/, ''),
      preview: file.type === 'application/pdf' ? '' : URL.createObjectURL(file),
    }));
    setFloorPlans((current) => [...current, ...accepted]);
    if (incoming.length > accepted.length) setPlanError('Podés cargar hasta 3 planos.');
  };

  return (
    <Page>
      <Container>

        <Header>
          <Title>Fotos de la propiedad</Title>
          <Subtitle>
            Subí al menos 4 fotos nítidas. La primera será la portada de tu aviso.
          </Subtitle>
        </Header>

        <PhotoGuide>
          <strong><FiStar /> Fotos que muestran mejor tu propiedad</strong>
          <div>
            <span><FiSun /> Elegí luz natural siempre que puedas.</span>
            <span><FiMaximize2 /> Mostrá cada ambiente completo.</span>
            <span><FiHome /> Usá la fachada o la vista principal como portada.</span>
          </div>
        </PhotoGuide>

        <TopBar>
          <BulkUpload type="button" onClick={openBulkPicker}>
            <FiUpload />
            <span className="desktop-label">Seleccionar varias fotos</span>
            <span className="mobile-label">Elegí tus fotos</span>
          </BulkUpload>
          <input
            ref={bulkInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif"
            multiple
            hidden
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />

          <Counter>
            {filled} / {SLOTS.length} fotos
          </Counter>
        </TopBar>

        <Hint>
          Arrastrá imágenes desde tu computadora o movelas para cambiar el orden.
        </Hint>
        {fileError && <FileError role="alert">{fileError}</FileError>}

        {filled === 0 && <MobilePhotoIntro>
          Podés elegir una o varias a la vez. Necesitás al menos 4 para continuar.
        </MobilePhotoIntro>}

        <ExtrasPanel>
          <ExtrasHeader><strong>Material adicional</strong><span>Opcional</span></ExtrasHeader>
          <ExtraTabs>
            <button type="button" className={activeExtra === 'video' ? 'active' : ''} onClick={() => setActiveExtra(activeExtra === 'video' ? null : 'video')}><FiVideo />Video</button>
            <button type="button" className={activeExtra === 'plans' ? 'active' : ''} onClick={() => setActiveExtra(activeExtra === 'plans' ? null : 'plans')}><FiFileText />Planos</button>
            <button type="button" className={activeExtra === 'tour' ? 'active' : ''} onClick={() => setActiveExtra(activeExtra === 'tour' ? null : 'tour')}><FiMap />Recorrido virtual</button>
          </ExtraTabs>
        {activeExtra === 'video' && <VideoSection>
          <div><strong>Video de la propiedad</strong><span>Opcional · 1 video de hasta 100 MB</span></div>
          {video ? (
            <VideoPreview>
              <video src={video.preview || video.url} controls playsInline />
              <button type="button" onClick={() => setVideo(null)}>Quitar video</button>
            </VideoPreview>
          ) : (
            <VideoUpload>
              <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(event) => addVideo(event.target.files?.[0])} />
              <span>Subir recorrido en video</span>
              <small>MP4, MOV o WebM</small>
            </VideoUpload>
          )}
          {videoError && <VideoError role="alert">{videoError}</VideoError>}
        </VideoSection>}

        {activeExtra === 'plans' && <PlansSection>
          <div><strong>Planos de la propiedad</strong><span>Sumá hasta 3 archivos.</span></div>
          <PlanUpload>
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" multiple onChange={(event) => { addFloorPlans(event.target.files); event.target.value = ''; }} />
            <span>Subir plano</span><small>PDF, JPG, PNG o WebP · hasta 3 archivos</small>
          </PlanUpload>
          {floorPlans.length > 0 && <PlanList>{floorPlans.map((plan, index) => <PlanItem key={`${plan.title}-${index}`}>
            {plan.type === 'image' ? <img src={plan.preview || plan.url} alt={`Plano: ${plan.title || index + 1}`} /> : <b>PDF</b>}
            <span>{plan.title || `Plano ${index + 1}`}</span>
            <button type="button" onClick={() => setFloorPlans((current) => current.filter((_, itemIndex) => itemIndex !== index))}>Quitar</button>
          </PlanItem>)}</PlanList>}
          {planError && <VideoError role="alert">{planError}</VideoError>}
        </PlansSection>}
        {activeExtra === 'tour' && <TourSection>
          <strong>Recorrido virtual</strong><span>Agregá un enlace de Matterport, Kuula o Street View.</span>
          <TourInput value={virtualTourUrl} onChange={(event) => setVirtualTourUrl(event.target.value)} type="url" placeholder="Enlace del recorrido virtual (Matterport, Kuula o Street View)" />
        </TourSection>}
        </ExtrasPanel>

        <Grid
          $externalDrag={externalDrag}
          onDragEnter={(event) => {
            if (event.dataTransfer?.types?.includes('Files')) {
              event.preventDefault();
              setExternalDrag(true);
            }
          }}
          onDragOver={(event) => {
            if (event.dataTransfer?.types?.includes('Files')) event.preventDefault();
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setExternalDrag(false);
          }}
          onDrop={(event) => {
            if (!event.dataTransfer?.files?.length) return;
            event.preventDefault();
            event.stopPropagation();
            setExternalDrag(false);
            addFiles(event.dataTransfer.files);
          }}
        >
          {externalDrag && <DropPrompt>Soltá las fotos acá</DropPrompt>}

          {SLOTS.map((label, index) => {

            const photo = photos[index];

            return (
              <Slot
                key={label}
                $filled={!!photo}
                draggable={!!photo}
                cover={index === 0 && photo}
                dragging={dragIndex === index}
                over={overIndex === index}
                selected={selectedIndex === index}
                onDragStart={(e) => onDragStart(e, index)}
                onDragOver={(e) => onDragOver(e, index)}
                onDrop={(event) => {
                  if (event.dataTransfer?.files?.length) {
                    event.preventDefault();
                    event.stopPropagation();
                    setExternalDrag(false);
                    addFiles(event.dataTransfer.files, photo ? null : index);
                  } else {
                    onDropReorder(index);
                  }
                }}
                onClick={() => {

                  if (photo) setSelectedIndex(index);
                  else inputRefs.current[index].click();

                }}
              >

                {!photo ? (

                  <Empty>

                    <span className="slot-label">{label}</span>

                    <input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="file"
                      accept="image/*"
                      onChange={(e) => { addFiles(e.target.files, index); e.target.value = ''; }}
                    />

                  </Empty>

                ) : (

                  <Image>

                    <img src={photo.preview} alt={label} />

                    <Hover className="slot-label">{label}</Hover>

                    {index === 0 && <Cover>Portada</Cover>}

                    <Remove
                      type="button"
                      aria-label={`Eliminar ${label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        removePhoto(index);
                      }}
                    >
                      ✕
                    </Remove>

                    <MobilePhotoActions>
                      <button
                        type="button"
                        aria-label="Mover foto hacia atrás"
                        disabled={index === 0 || !photos[index - 1]}
                        onClick={(event) => { event.stopPropagation(); movePhoto(index, -1); }}
                      >←</button>
                      <button
                        type="button"
                        aria-label="Mover foto hacia adelante"
                        disabled={!photos[index + 1]}
                        onClick={(event) => { event.stopPropagation(); movePhoto(index, 1); }}
                      >→</button>
                    </MobilePhotoActions>

                  </Image>

                )}

              </Slot>
            );

          })}

        </Grid>

        <Actions>

          <Secondary type="button" onClick={onBack}>
            Atrás
          </Secondary>

          <Primary
            type="button"
            $incomplete={!canContinue}
            onClick={() => {
              if (!canContinue) {
                openBulkPicker();
                return;
              }
              onSubmit({ images: photos.filter(Boolean), video, floorPlans, virtualTourUrl: virtualTourUrl.trim() });
            }}
          >
            {canContinue
              ? 'Continuar'
              : `Agregá ${4 - filled} fotos más`}
          </Primary>

        </Actions>

      </Container>
    </Page>
  );
}
/* ======================= STYLES ======================= */

const glow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(198,111,78,0.9); }
  70% { box-shadow: 0 0 0 14px rgba(198,111,78,0); }
  100% { box-shadow: 0 0 0 0 rgba(198,111,78,0); }
`;

const Page = styled.div`
  padding-top: 200px;
  background: #f3f0ea;
  min-height: 100vh;
  min-width:0;
  overflow-x:hidden;
  @media(max-width:1200px){padding-top:28px;}
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px 160px;
  @media(max-width:1200px){padding:0 16px 150px;}
`;

const Header = styled.div`
  margin-bottom: 24px;
  @media(max-width:1200px){margin-bottom:18px;}
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 600;
  @media(max-width:1200px){font-size:32px;line-height:1.05;}
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
`;
const PhotoGuide = styled.aside`display:grid;gap:10px;margin:0 0 20px;padding:14px 16px;border-radius:16px;background:#fff5f0;color:#637579;strong{display:flex;align-items:center;gap:8px;color:#29474f;font-size:14px;}strong svg{color:#c66f4e;}div{display:grid;gap:7px;}span{display:flex;align-items:center;gap:8px;font-size:13px;line-height:1.4;}span svg{width:16px;height:16px;flex:0 0 auto;color:#c66f4e;}`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  gap:12px;
  @media(max-width:1200px){
    align-items:center;
    margin-bottom:14px;
    padding:14px;
    border:1px solid #e8d7cd;
    border-radius:21px;
    background:linear-gradient(135deg,#fff7f1,#fffdfa);
    box-shadow:0 12px 28px rgba(105,67,47,.06);
  }
`;

const Counter = styled.div`
  font-size: 14px;
  color: #888;
  font-weight: 500;
`;

const Hint = styled.div`
  font-size: 14px;
  color: #999;
  margin-bottom: 22px;
  @media(max-width:1200px){display:none;}
`;
const ExtrasPanel=styled.section`margin:24px 0 34px;padding:18px;border:1px solid #dce4e1;border-radius:18px;background:#fff;`;
const ExtrasHeader=styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:13px;strong{color:#29474f;font-size:16px;}span{color:#77878a;font-size:12px;font-weight:700;}`;
const ExtraTabs=styled.div`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;button{display:flex;min-height:62px;align-items:center;justify-content:center;gap:7px;padding:8px;border:1px solid #dce4e1;border-radius:13px;background:#fff;color:#5c7074;font:inherit;font-size:12px;font-weight:750;cursor:pointer;}svg{width:18px;height:18px;color:#b45f40;}.active{border-color:#c66f4e;background:#fff5f0;color:#29474f;}@media(max-width:430px){grid-template-columns:1fr;button{justify-content:flex-start;padding-left:15px;}}`;
const VideoSection = styled.section`display:grid;gap:14px;margin:0 0 20px;>div:first-child strong,>div:first-child span{display:block;}strong{color:#29474f;font-size:18px;}span{margin-top:4px;color:#718083;font-size:14px;}@media(max-width:1200px){gap:10px;strong{font-size:16px;}span{font-size:13px;}}`;
const VideoUpload = styled.label`display:grid;min-height:145px;place-content:center;justify-items:center;color:#29474f;background:#fff;border:2px dashed rgba(41,71,79,.18);border-radius:17px;cursor:pointer;input{display:none;}span{font-size:16px;font-weight:700;}small{margin-top:5px;color:#879294;}@media(max-width:1200px){min-height:72px;padding:10px;border-style:solid;border-color:#d8e4de;border-radius:14px;background:#fff;span{font-size:14px;}small{margin-top:3px;font-size:12px;}}`;
const VideoPreview = styled.div`position:relative;overflow:hidden;background:#20373c;border-radius:17px;video{display:block;width:100%;max-height:390px;object-fit:contain;}button{position:absolute;top:12px;right:12px;padding:9px 13px;color:#fff;background:rgba(25,42,46,.82);border:0;border-radius:999px;font:inherit;font-size:13px;cursor:pointer;}`;
const VideoError = styled.p`margin:0;color:#a74832;font-size:14px;font-weight:650;`;
const PlansSection = styled.section`display:grid;gap:14px;margin:0 0 20px;>div:first-child strong,>div:first-child span{display:block;}strong{color:#29474f;font-size:18px;}span{margin-top:4px;color:#718083;font-size:14px;}@media(max-width:1200px){gap:10px;strong{font-size:16px;}span{font-size:13px;}}`;
const TourSection=styled.section`display:grid;gap:10px;margin-top:18px;strong,span{display:block;}strong{color:#29474f;font-size:16px;}span{color:#718083;font-size:13px;}`;
const PlanUpload = styled.label`display:grid;min-height:88px;place-content:center;justify-items:center;color:#29474f;background:#fff;border:2px dashed rgba(41,71,79,.18);border-radius:17px;cursor:pointer;input{display:none;}span{font-size:16px;font-weight:700;}small{margin-top:5px;color:#879294;}@media(max-width:1200px){min-height:70px;padding:10px;border-style:solid;border-color:#d8e4de;border-radius:14px;background:#fff;span{font-size:14px;}small{margin-top:3px;font-size:12px;}}`;
const PlanList = styled.div`display:flex;flex-wrap:wrap;gap:10px;`;
const PlanItem = styled.div`display:grid;grid-template-columns:42px minmax(0,1fr) auto;align-items:center;gap:10px;min-width:min(100%,270px);padding:8px;border:1px solid #dbe4e1;border-radius:12px;background:#fff;img,b{width:42px;height:42px;border-radius:8px;object-fit:cover;}b{display:grid;place-items:center;background:#f5e5dd;color:#a95336;font-size:11px;}span{min-width:0;margin:0;overflow:hidden;color:#40565b;font-size:13px;text-overflow:ellipsis;white-space:nowrap;}button{border:0;background:transparent;color:#a95336;font:inherit;font-size:12px;font-weight:700;cursor:pointer;}`;
const TourInput = styled.input`width:100%;height:52px;padding:0 15px;border:1px solid #d8e2df;border-radius:13px;background:#fff;color:#29474f;font:inherit;box-sizing:border-box;&:focus{outline:none;border-color:#c66f4e;box-shadow:0 0 0 3px rgba(198,111,78,.13);}`;
const FileError = styled.p`margin:-10px 0 22px;padding:11px 14px;color:#93452f;background:#f7e7df;border-radius:12px;font-size:13px;font-weight:650;`;

const BulkUpload = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 14px 22px;
  border:0;
  border-radius: 18px;
  font:inherit;
  box-shadow: 0 12px 30px rgba(0,0,0,0.08);
  cursor: pointer;
  font-weight: 500;
  max-width:100%;

  .mobile-label { display:none; }

  @media(max-width:1200px){
    flex:1;
    min-height:58px;
    justify-content:center;
    color:white;
    background:linear-gradient(135deg,#c66f4e,#b55e40);
    border-radius:15px;
    box-shadow:0 12px 24px rgba(198,111,78,.24);
    font-weight:750;
    svg{width:18px;height:18px;}
    .desktop-label { display:none; }
    .mobile-label { display:inline; }
  }

`;

const Grid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 22px;
  padding: ${({ $externalDrag }) => $externalDrag ? '14px' : '0'};
  outline: ${({ $externalDrag }) => $externalDrag ? '3px dashed #C66F4E' : '3px dashed transparent'};
  outline-offset: 8px;
  border-radius: 24px;
  transition: padding .18s ease, outline-color .18s ease;
  @media(max-width:520px){grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;}
`;
const DropPrompt = styled.div`
  position:absolute;inset:0;z-index:20;display:grid;place-items:center;color:#93452f;background:rgba(255,247,242,.9);
  border-radius:22px;font-size:20px;font-weight:750;pointer-events:none;
`;

const Slot = styled.div`
  background: white;
  border-radius: 22px;
  overflow: hidden;
  position: relative;
  cursor: pointer;

  border: ${({ cover, selected }) =>
    selected
      ? '3px solid #111'
      : cover
      ? '2px solid #C66F4E'
      : '2px solid transparent'};

  box-shadow: ${({ selected }) =>
    selected
      ? '0 0 0 3px rgba(0,0,0,0.15)'
      : '0 14px 40px rgba(0,0,0,0.08)'};

  transform: ${({ dragging }) =>
    dragging ? 'scale(0.95)' : 'scale(1)'};

  transition: all 0.15s ease;

  @media(max-width:900px){.slot-label{display:none;}}

  @media(max-width:900px){
    display:${({ $filled }) => $filled ? 'block' : 'none'};
    border-radius:16px;
  }

  ${({ dragging }) =>
    dragging &&
    css`
      animation: ${glow} 1.1s ease-out infinite;
    `}

  ${({ over }) =>
    over &&
    css`
      outline: 3px dashed #C66F4E;
      outline-offset: -6px;
      background: #fff7f2;
    `}
`;

const Empty = styled.div`
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 14px;
  border: 2px dashed #ddd;
  text-align:center;
  padding:10px;
  @media(max-width:520px){height:132px;}

  input {
    display: none;
  }
`;

const Image = styled.div`
  position: relative;

  img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    display:block;
    @media(max-width:520px){height:132px;}
  }

  &:hover div {
    opacity: 1;
  }
`;

const MobilePhotoActions = styled.div`
  display:none;

  @media(max-width:520px){
    position:absolute;
    right:8px;
    bottom:8px;
    z-index:4;
    display:flex;
    gap:5px;

    button{
      width:32px;
      height:32px;
      padding:0;
      border:1px solid rgba(255,255,255,.38);
      border-radius:50%;
      color:white;
      background:rgba(24,39,43,.82);
      font-size:16px;
      font-weight:700;
    }

    button:disabled{opacity:.35;}
  }
`;

const Hover = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0,0,0,0.6);
  color: white;
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 14px;
  opacity: 0;
  @media(max-width:520px){display:none;}
`;

const Remove = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0,0,0,0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  font-size: 14px;
  cursor: pointer;
  opacity: 1;
  z-index:5;
`;

const Cover = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: #C66F4E;
  color: white;
  font-size: 14px;
  padding: 6px 10px;
  border-radius: 12px;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 56px;
  gap:14px;
  @media(max-width:900px){position:fixed;z-index:1100;right:0;bottom:0;left:0;display:grid;grid-template-columns:1fr 1.45fr;gap:10px;margin:0;padding:13px 16px calc(13px + env(safe-area-inset-bottom));border-top:1px solid rgba(41,71,79,.1);background:rgba(255,253,249,.96);backdrop-filter:blur(14px);button{width:100%;min-height:52px;padding:0;border-radius:15px;font:inherit;font-size:.92rem;font-weight:850;}button:first-child{border:1px solid #d5dfdc;background:#fff;color:#51696e;box-shadow:none;}button:last-child{border:0;background:#c66f4e;color:#fff;box-shadow:0 9px 20px rgba(198,111,78,.2);}}
`;

const Primary = styled.button`
  background: ${({ $incomplete }) => $incomplete ? '#29474f' : 'linear-gradient(135deg, #C66F4E 0%, #ff9d5a 100%)'};
  color: white;
  padding: 16px 44px;
  border-radius: 999px;
  border: none;
  font-weight: 600;
  font-size: 17px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ $incomplete }) => $incomplete ? '0 12px 28px rgba(41,71,79,.2)' : '0 12px 28px rgba(198,111,78,.35)'};
  max-width:100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 38px rgba(198,111,78, 0.45);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 10px 20px rgba(198,111,78, 0.35);
  }
`;

const Secondary = styled.button`
  background: none;
  border: none;
  font-size: 15px;
  color: #666;
  cursor: pointer;
`;
const MobilePhotoIntro = styled.p`display:none;@media(max-width:900px){display:block;margin:0 0 20px;color:#718083;font-size:13px;line-height:1.45;}`;
